import { FormikProps } from 'formik'
import styled from 'styled-components'
import { Button } from 'sutra/components/Button'
import { FormItem } from 'sutra/components/Form'
import { Input } from 'sutra/components/Input/Input'
import { Select } from 'sutra/components/Select/Select'

import { AccountSelectContainer } from 'features/accounts/Container'
import { CardNew } from 'features/project-form/components/CardNew'
import { FormTitle } from 'features/project-form/components/FormTitle'
import ProjectSourceVirtualizedList from 'features/project-form/components/ProjectSourceVirtualizedList'
import { FormValues } from 'features/project-form/features/new/Container'
import { Product } from 'features/project-form/features/product/types'
import {
  ProjectSourceFile,
  ProjectSourceURL,
} from 'features/project-form/features/source/types'
import {
  DATA_TYPE_LABELS,
  PROJECT_DATA_TYPES,
  PROJECT_USAGE_VALUES,
  USAGE_LABELS,
} from 'features/projects/constants'
import { PressableToggleButton } from 'src/components/ui/PressableToggleButton'

type Props = {
  handleOpenSource: () => void
  handleOpenProduct: () => void
  formik: FormikProps<FormValues>
  isSubmitDisabled: boolean
  isEmptyProject?: boolean
  sources: Array<ProjectSourceFile | ProjectSourceURL>
  products: Array<Product>
  stopWords: string[]
  setStopWords: (s: string[]) => void
  setSourceToEditIndex: (n: number) => void
  setProductToEditIndex: (n: number) => void
}

export const NewProject = ({
  formik,
  products,
  handleOpenSource,
  handleOpenProduct,
  isSubmitDisabled,
  isEmptyProject,
  stopWords,
  setStopWords,
  setSourceToEditIndex,
  setProductToEditIndex,
}: Props): JSX.Element => {
  return (
    <Wrapper>
      <FormTitle>Add New Dataset</FormTitle>
      <FormItem
        label="Title"
        validateStatus={formik.errors.title ? 'error' : ''}
        help={formik.errors.title}
        wrapperCol={{ span: 10 }}
      >
        <Input
          $error={!!formik.errors.title}
          name="title"
          onChange={formik.handleChange}
          value={formik.values.title}
        />
      </FormItem>
      <FormItem
        label="Description"
        validateStatus={formik.errors.description ? 'error' : ''}
        help={formik.errors.description}
        wrapperCol={{ span: 16 }}
      >
        <Input
          $error={!!formik.errors.description}
          name="description"
          onChange={formik.handleChange}
          value={formik.values.description}
        />
      </FormItem>
      <FormItem
        label="Usage"
        validateStatus={formik.errors.usage ? 'error' : ''}
        help={formik.errors.usage}
        wrapperCol={{ span: 16 }}
      >
        <ToggleList>
          {PROJECT_USAGE_VALUES.map((u) => (
            <PressableToggleButton
              key={u}
              pressed={formik.values.usage === u}
              onPressedChange={(nextPressed) =>
                formik.setFieldValue('usage', nextPressed ? u : null)
              }
            >
              {USAGE_LABELS[u]}
            </PressableToggleButton>
          ))}
        </ToggleList>
      </FormItem>
      <FormItem
        label="Data Types"
        validateStatus={formik.errors.data_types ? 'error' : ''}
        help={formik.errors.data_types}
        wrapperCol={{ span: 16 }}
      >
        <ToggleList>
          {PROJECT_DATA_TYPES.map((dt) => (
            <PressableToggleButton
              key={dt}
              pressed={formik.values.data_types.includes(dt)}
              onPressedChange={() => {
                const current = formik.values.data_types
                const next = current.includes(dt)
                  ? current.filter((x) => x !== dt)
                  : [...current, dt]
                formik.setFieldValue('data_types', next)
              }}
            >
              {DATA_TYPE_LABELS[dt]}
            </PressableToggleButton>
          ))}
        </ToggleList>
      </FormItem>
      <FormItem
        label="Visibility"
        validateStatus={formik.errors.is_internal ? 'error' : ''}
        help={formik.errors.is_internal}
        wrapperCol={{ span: 16 }}
      >
        <ToggleList>
          <PressableToggleButton
            pressed={formik.values.is_internal === true}
            onPressedChange={(nextPressed) =>
              formik.setFieldValue('is_internal', nextPressed ? true : null)
            }
          >
            Internal
          </PressableToggleButton>
          <PressableToggleButton
            pressed={formik.values.is_internal === false}
            onPressedChange={(nextPressed) =>
              formik.setFieldValue('is_internal', nextPressed ? false : null)
            }
          >
            External
          </PressableToggleButton>
        </ToggleList>
      </FormItem>
      <FormItem label="Account" wrapperCol={{ span: 10 }}>
        <AccountSelectContainer
          value={formik.values.account_uuid}
          onChange={(uuid) => formik.setFieldValue('account_uuid', uuid)}
        />
      </FormItem>
      {!isEmptyProject && (
        <>
          <FormItem label="Stop Words" wrapperCol={{ span: 16 }}>
            <Select
              mode="tags"
              options={[]}
              style={{ width: '100%' }}
              value={stopWords}
              onChange={(s) =>
                setStopWords(s.filter((word) => word.trim().length > 0))
              }
              tokenSeparators={[',']}
            />
          </FormItem>
          <FormItem label="Products" wrapperCol={{ span: 16 }}>
            <SourcesRow>
              {formik.values.title.length === 0 && (
                <Layer data-testid="products-disabled-overlay" />
              )}
              <Sources>
                <SourceCardWrapper onClick={handleOpenProduct}>
                  <CardNew text={'Add Product'} />
                </SourceCardWrapper>
              </Sources>
              <ProjectSourceVirtualizedList
                items={formik.values.products}
                handleOpen={handleOpenProduct}
                setEditIndex={setProductToEditIndex}
                formik={formik}
              />
            </SourcesRow>
          </FormItem>
          <FormItem
            label="Sources"
            wrapperCol={{ span: 16 }}
            validateStatus={formik.errors.sources ? 'error' : ''}
          >
            <SourcesRow>
              {(formik.values.title.length === 0 || products.length === 0) && (
                <Layer data-testid="sources-disabled-overlay" />
              )}
              <Sources>
                <SourceCardWrapper onClick={handleOpenSource}>
                  <CardNew />
                </SourceCardWrapper>
              </Sources>
              <ProjectSourceVirtualizedList
                items={formik.values.sources}
                handleOpen={handleOpenSource}
                setEditIndex={setSourceToEditIndex}
                formik={formik}
              />
            </SourcesRow>
          </FormItem>
        </>
      )}

      <FormItem style={{ marginTop: 'auto', marginBottom: 0 }}>
        <SubmitButton
          disabled={isSubmitDisabled}
          variant="primary"
          type="submit"
        >
          Submit
        </SubmitButton>
      </FormItem>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 800px;
  height: 600px;
  overflow: auto;
  display: flex;
  flex-direction: column;

  @media screen and (max-width: 1024px) {
    width: auto;
    min-width: 300px;
  }
`
const SubmitButton = styled(Button)`
  display: flex;
  width: fit-content;
  margin: var(--default-padding-half) auto 0;
`
const SourcesRow = styled.div`
  display: flex;
  position: relative;
`
const Sources = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: stretch;
  flex-wrap: wrap;
  position: relative;

  @media screen and (max-width: 1024px) {
    flex-direction: column;
    justify-content: flex-start;
  }
`
const SourceCardWrapper = styled.div`
  padding: 10px;
  display: flex;

  @media screen and (max-width: 1024px) {
    width: 100%;
  }
`
const Layer = styled.div`
  background: rgba(255, 255, 255, 0.7);
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
`
const ToggleList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`
